/*
 * Copyright (c) 2020 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */

import { createApi } from "@reduxjs/toolkit/query/react";

import { getCSRFTokenHeader } from "common/hooks";
import { replacer, transformResponse } from "common/util";
import TimeSlots from "pages/Schedule/types/TimeSlots";
import { CLAIM_SHIFT_INTENTION_API_URL } from "store/api/constants";
import { dynamicBaseQuery } from "store/api/utils/dynamicBaseQuery";
import { ReserveShiftRequest, ReserveShiftResponse, ShiftBatch } from "store/slices/shiftBatches";
import { ShiftClaim } from "store/slices/shiftClaimsPolling/types";
import { CONTENT_TYPE_JSON, RequestMethod } from "store/types";

interface RequestPayload {
    url: string;
    data: unknown;
}

export interface ApplyClaimsResponse {
    shiftId: string;
    licensePlate: string | null;
    vanId: string | null;
    vanVisualId: string | null;
}

export const api = createApi({
    baseQuery: dynamicBaseQuery,
    reducerPath: "api",
    refetchOnMountOrArgChange: true,
    tagTypes: ["Schedule"],
    endpoints: (builder) => ({
        getSchedule: builder.query<TimeSlots, string>({
            providesTags: ["Schedule"],
            query: (url) => ({
                headers: CONTENT_TYPE_JSON,
                url,
            }),
            transformResponse: (response: TimeSlots) => ({
                schedule: transformResponse(response.schedule),
            }),
        }),
        unApplyShift: builder.mutation<{ shift_id: string }, RequestPayload>({
            query: ({ url, data }) => ({
                body: JSON.parse(JSON.stringify(data, replacer)),
                headers: {
                    ...getCSRFTokenHeader(),
                },
                method: RequestMethod.POST,
                url,
            }),
        }),
        clearShiftIntention: builder.mutation<void, string>({
            query: (shiftId: string) => ({
                body: JSON.parse(JSON.stringify({ shiftId }, replacer)),
                headers: {
                    ...getCSRFTokenHeader(),
                },
                method: RequestMethod.DELETE,
                url: CLAIM_SHIFT_INTENTION_API_URL,
                responseType: "text",
            }),
        }),
        getShiftClaimsStatuses: builder.query<ShiftClaim[], { url: string }>({
            query: ({ url }) => ({ url }),
            transformResponse,
        }),
        getShiftBatches: builder.query<ShiftBatch[], { url: string }>({
            query: ({ url }) => ({ url }),
            transformResponse,
        }),
        reserveShift: builder.mutation<ReserveShiftResponse, ReserveShiftRequest>({
            query: ({ url, data }) => ({
                body: data,
                headers: {
                    ...CONTENT_TYPE_JSON,
                },
                method: RequestMethod.POST,
                url,
            }),
            transformResponse,
        }),
    }),
});

export const {
    useGetScheduleQuery,
    useUnApplyShiftMutation,
    useClearShiftIntentionMutation,
    useGetShiftBatchesQuery,
    useLazyGetShiftBatchesQuery,
    useReserveShiftMutation,
    useLazyGetShiftClaimsStatusesQuery,
} = api;
